"use client";

import { useEffect, useState } from "react";
import type { LiveParticipantsReply } from "./session-control";

/**
 * L'intervalle entre deux relevés, en millisecondes.
 *
 * Trois secondes : la liste ne sert qu'à regarder, et personne n'a besoin d'y
 * voir une arrivée à l'instant. Les mesures qu'elle porte ne bougent qu'au
 * rythme des annonces, qui partent toutes les deux secondes hors jam.
 */
export const PARTICIPANTS_POLL_MS = 3_000;

/**
 * Qui est dans la séance, relu pendant qu'on le regarde.
 *
 * # Seulement quand on regarde
 *
 * La liste et les mesures de chacun voyageaient dans la métadonnée de salle,
 * vers toute la salle, pour un panneau qu'on ouvre de temps en temps. Elles se
 * demandent maintenant à la route des participants (voir
 * `fetchLiveParticipants`), et seulement tant que `active` le dit : le panneau
 * de séance reste monté à largeur nulle quand il est fermé, et un relevé qui
 * tournerait là coûterait au service une requête toutes les trois secondes,
 * par participant, pour une liste que personne ne voit.
 *
 * # Un relevé à la fois
 *
 * Le suivant s'arme quand la réponse est là, pas sur un intervalle : un
 * service lent n'empile pas les requêtes, et une réponse ancienne ne peut pas
 * revenir après une plus récente pour remettre une liste périmée.
 *
 * # Ce qui reste à l'écran
 *
 * Un relevé en échec ne vide pas la liste : il ne dit pas que les autres sont
 * partis. Une réponse est rangée sous la salle qui l'a demandée, et une autre
 * salle ne la voit jamais — le panneau rouvert dans une séance neuve attend
 * son premier relevé plutôt que de montrer les noms de la précédente.
 */
export function useLiveParticipants(
  fetchParticipants: () => Promise<LiveParticipantsReply | null>,
  room: string | null,
  active: boolean,
): LiveParticipantsReply | null {
  const [held, setHeld] = useState<{ room: string; reply: LiveParticipantsReply } | null>(null);

  useEffect(() => {
    if (!active || room === null) {
      return;
    }

    let stopped = false;
    let handle: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      const reply = await fetchParticipants();

      if (stopped) {
        return;
      }

      if (reply !== null) {
        setHeld({ room, reply });
      }

      handle = setTimeout(() => void poll(), PARTICIPANTS_POLL_MS);
    };

    // Tout de suite, puis en cadence : un panneau qui s'ouvre sur trois
    // secondes d'attente a l'air cassé.
    void poll();

    return () => {
      stopped = true;

      if (handle !== null) {
        clearTimeout(handle);
      }
    };
  }, [active, fetchParticipants, room]);

  if (held === null || held.room !== room) {
    return null;
  }

  return held.reply;
}
